import { Component, Input, OnChanges, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController, PopoverController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { PopupSelectOperationComponent } from "src/components/popup/popup-select-operation/popup-select-operation.component";

import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { WeeklyPlanning } from "src/model/class/general classes/weekly-planning";
import { WeeklyPlanningAction } from "src/model/class/general classes/weekly-planning-action";
import { Settings } from "src/model/class/settings";
import { ClickActionType } from "src/model/enum/enumCustomInterface";

import { WeeklyPlanningButtonsActions } from "src/model/enum/enumWeeklyPlanning";
import { iWeeklyPlanningAction } from "src/model/interface/iWeekly-planning-action";
import { AddressService } from "src/service/address.service";
import { AlertService } from "src/service/alert.service";
import { EventService } from "src/service/event.service";
import { LoaderService } from "src/service/loader.service";
import { NetworkService } from "src/service/network.service";
import { PopupService } from "src/service/popup.service";
import { ToastService } from "src/service/toast.service";
import { WeeklyPlanningService } from "src/service/weekly-planning.service";
import { canEditWeeklyPlanningDailySector } from "src/state/selectors/server-settings-selector";
import { DayPeriodChangeComponent } from "../day-period-change/day-period-change.component";
import { PopoverObjectInfoComponent } from "../popover-object-info/popover-object-info.component";
import { WeekDayChooseComponent } from "../week-day-choose/week-day-choose.component";
import { Address } from "src/components/shared-components/class/component-classes/address";
import { AddressMethod } from "src/components/shared-components/interfaces/address-method";
import { iAddressSearch } from "src/components/shared-components/interfaces/iAddress-search";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { ToastType } from "src/components/shared-components/enum/enumMisc";

@Component({
    selector: "app-weekly-planning-single-day",
    templateUrl: "./weekly-planning-single-day.component.html",
    styleUrls: ["./weekly-planning-single-day.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WeeklyPlanningSingleDayComponent
  implements OnInit, OnChanges, OnDestroy {
  //#region Subcriptions to "_store.select(...)" Observables
  private _selectServerSettingsUserSub: Subscription = null;

  //#endregion

  private _translateSubscription: Subscription = null;

  private _weeklyPlanningSub: Subscription = null;
  private _addressWeeklyPlanning: Address;

  private _lblTitleChooseOperation: string = "";
  private _lblMessageChooseOperation: string = "";
  private _lblOptionChangeValue: string = "";
  private _lblOptionChangeColor: string = "";
  private _lblOptionAddSlice: string = "";
  private _lblMOp = "*";
  private _lblOp2 = "*";
  private _lblRead = "*";
  private _lblWrite = "*";
  private _lblWriteAddress = "*";
  private _lblValueToWrite = "*";
  private _lblRemoveValue = "*";
  private _lblValueAlreadyUsed = "*";
  private _lblNoChange = "*";

  private from: string = "";
  private to: string = "";
  private monday: string = "";
  private tuesday: string = "";
  private wednesday: string = "";
  private thursday: string = "";
  private friday: string = "";
  private saturday: string = "";
  private sunday: string = "";
  private _generalSettings: Settings = null;

  /**
   * Gneric array, it holds a list of geric objects, each detailing the characteristic of a particular section of the echarts
   * graphic component; each of this object (and the echarts section it describes) should correspond to a specific "weeklyPlanningAction".
   */
  private echartData = [];

  private _chartInstance;
  private _networkServiceSubscription: Subscription = null;

  /**The list of minimal "unit-time-sectors" in the specific day associated with the currently selected weekly-planning object; they should be 96 temporal sectors constutued of 15 minutes each.*/
  public weeklyPlanningActions: WeeklyPlanningAction[] = null;
  public chartTitle: string = "";
  public editWeeklyPlannings: boolean = false;

  /** Translated week-day name shown in the card header (the chart no longer draws a title). */
  public dayLabel: string = "";

  public options: any = {
    tooltip: {
      trigger: "item",
      backgroundColor: "rgba(255, 255, 255, 0.96)",
      borderColor: "rgba(31, 41, 51, 0.08)",
      borderWidth: 1,
      padding: [8, 12],
      textStyle: {
        color: "#1f2933",
        fontSize: 12,
      },
      extraCssText:
        "box-shadow: 0 10px 26px rgba(31, 41, 51, 0.14); border-radius: 10px;",
      formatter: (params) => {
        return (
          "<b>" +
          params.data.name +
          "</b><br/>" +
          this.from +
          this.formatMinute(params.data.startMinute) +
          this.to +
          this.formatMinute(params.data.endMinute)
        );
      },
    },
    legend: {
      bottom: 0,
      left: "center",
      icon: "circle",
      itemWidth: 9,
      itemHeight: 9,
      itemGap: 14,
      textStyle: {
        color: "#5f6b7a",
        fontSize: 12,
      },
      data: [],
    },
    series: [
      {
        type: "pie",
        top: 0,
        bottom: 26,
        radius: ["58%", "82%"],
        center: ["50%", "50%"],
        avoidLabelOverlap: false,
        /* Rounded segments separated by thin gaps: the gaps match the card surface. */
        itemStyle: {
          borderRadius: 7,
          borderColor: "#ffffff",
          borderWidth: 3,
        },
        label: {
          show: false,
          position: "center",
        },
        emphasis: {
          scale: true,
          scaleSize: 5,
          label: {
            show: true,
            formatter: (params) =>
              "{n|" +
              params.name +
              "}\n{t|" +
              this.formatMinute(params.data.startMinute) +
              " – " +
              this.formatMinute(params.data.endMinute) +
              "}",
            rich: {
              n: {
                fontSize: 14,
                fontWeight: 700,
                color: "#1f2933",
                lineHeight: 20,
              },
              t: {
                fontSize: 11,
                color: "#8a94a0",
              },
            },
          },
        },
        labelLine: {
          show: false,
        },
        data: this.echartData,
        animationType: "scale",
        animationEasing: "cubicOut",
        animationDuration: 550,
      },
    ],
  };

  public chartStyle = {
    height: "300px",
    "backface-visibility": "hidden",
    transform: "translateZ(0)",
    "-webkit-font-smoothing": "subpixel-antialiased",
  };

  @Input() currentWeeklyPlanning: WeeklyPlanning = null;
  @Input() day: number = 1;
  @Input() inCustomInterface: boolean = false;
  @Input() showShadow: boolean = true;

  /**
   * `true` while the host page is still loading the weekly-planning list: it tells the card to show a
   * loading placeholder instead of the "deleted" message when {@link currentWeeklyPlanning} is not yet resolved.
   */
  @Input() isLoading: boolean = false;

  /**
   * `true` until the chart holds its own data. The card renders as soon as the weekly planning is
   * known, but the series and the legend take two more requests (the address the values are named
   * after, then the day actions): without this the user was left watching an initialised but empty
   * echarts instance, i.e. a grey ring with no legend.
   */
  public isChartLoading: boolean = true;

  public toolBarColor: string = "primary";
  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  constructor(
    private _weeklyPlanningService: WeeklyPlanningService,
    private _modalController: ModalController,
    private _popoverCtrl: PopoverController,
    private _addressService: AddressService,
    private _translateService: TranslateService,
    private _networkService: NetworkService,
    private _alertService: AlertService,
    private _eventService: EventService,
    private _store: Store,
    private _popupService: PopupService,
    private _toastService: ToastService,
    private _loaderService: LoaderService
  ) { }

  ngOnInit() {
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeWeeklyPlanningService();
    this.subscribeNetworkServiceData();

    /** Editing the time-slices is the one weekly-planning permission a resident also holds. */
    this._selectServerSettingsUserSub = this._store
      .select(canEditWeeklyPlanningDailySector)
      .subscribe((canEditDailySector: boolean) => {
        this.editWeeklyPlannings = canEditDailySector;
      });

    this._eventService.OnReloadWeeklyPlanning.subscribe(() => {
      this._weeklyPlanningService.reloadServiceData();
    });
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();

    this._translateSubscription.unsubscribe();
    if (this._weeklyPlanningSub) this._weeklyPlanningSub.unsubscribe();
    this._networkServiceSubscription.unsubscribe();
  }

  async ngOnChanges() {
    if (
      this.currentWeeklyPlanning &&
      this.currentWeeklyPlanning != null &&
      this._chartInstance
    ) {
      this._addressWeeklyPlanning =
        this._addressService.getAddressByInstanceGUIDAndAddress(
          this.currentWeeklyPlanning.instanceguid,
          this.currentWeeklyPlanning.address
        );

      if (
        !this._addressWeeklyPlanning ||
        this._addressWeeklyPlanning == null ||
        this._addressWeeklyPlanning?.methods == ""
      ) {
        //if (!this._addressWeeklyPlanning || this._addressWeeklyPlanning == null) {
        let addressSearchObj: iAddressSearch = {
          Instanceguid: this.currentWeeklyPlanning.instanceguid,
          Address: this.currentWeeklyPlanning.address,
          Name: "",
          Language: this._translateService.getCurrentLang(),
        };
        await this._addressService
          .getAddressesByInstanceGUIDAndAddressAndName(addressSearchObj)
          .then((result: Array<Address>) => {
            this._addressWeeklyPlanning = new Address(result[0]);
          });
      }

      this.loadData();

      this.options.legend.data = [
        {
          name: this.getStateFromTName("T1"),
        },
        {
          name: this.getStateFromTName("T2"),
        },
        {
          name: this.getStateFromTName("T3"),
        },
        {
          name: this.getStateFromTName("T4"),
        },
        {
          name: this.getStateFromTName("T5"),
        },
      ];

      this._chartInstance.setOption(this.options);
    }
  }

  /**
   * When click on legend open a popup to choose what operation do
   * @param params legend item clicked
   */
  async onLegendSelectChanged(params) {
    this.suppressSelection(this._chartInstance, params);
    if (this.editWeeklyPlannings) {
      let buttons = [
        /* {
          name: this._lblOptionChangeColor,
          value: WeeklyPlanningButtonsActions.ChangeColor,
        }, */
        {
          name: this._lblOptionChangeValue,
          value: WeeklyPlanningButtonsActions.ChangeValue,
        },
        {
          name: this._lblOptionAddSlice,
          value: WeeklyPlanningButtonsActions.NewSlice,
        },
      ];

      let popover = await this._modalController.create({
        component: PopupSelectOperationComponent,
        componentProps: {
          Title: this._lblMessageChooseOperation,
          Buttons: buttons,
          onclose: () => {
            popover.dismiss();
          },
        },
        cssClass: "modal-operation",
        animated: true,
        showBackdrop: true,
      });

      popover.present();

      const { data } = await popover.onDidDismiss();

      if (data == WeeklyPlanningButtonsActions.NewSlice)
        this.askNewSlice(params.name);
      else if (data == WeeklyPlanningButtonsActions.ChangeColor) {
      } else if (data == WeeklyPlanningButtonsActions.ChangeValue) {
        let tChanged = this.getTNameFromState(params.name);

        if (tChanged == "T1") {
          this.openAddressValue(1);
        } else if (tChanged == "T2") {
          this.openAddressValue(2);
        } else if (tChanged == "T3") {
          this.openAddressValue(3);
        } else if (tChanged == "T4") {
          this.openAddressValue(4);
        } else if (tChanged == "T5") {
          this.openAddressValue(5);
        }
      }
    }
  }

  async onClick(params) {
    if (this.editWeeklyPlannings) {

      const modal = await this._modalController.create({
        component: DayPeriodChangeComponent,
        componentProps: {
          startPeriodIn: params.data.startMinute,
          endPeriodIn: params.data.endMinute,
          tValue: this.getTNameFromState(params.data.name),
          currentWeeklyPlanning: this.currentWeeklyPlanning,
          stringValue: this.options.legend.data,
        },
        cssClass: "modal-period-value",
      });

      modal.onDidDismiss().then(async (changedValue) => {
        if (changedValue.data != undefined && changedValue.data != null) {
          this.createNewSlice(
            changedValue.data.tValue,
            changedValue.data.startPeriod,
            changedValue.data.endPeriod,
            params.data.startMinute,
            params.data.endMinute
          );
        }
      });

      return await modal.present();
    }
  }

  /**Hook invocked when "echarts" ionic-angular compoent has finished initialising*/
  onChartInit(ec) {
    this._chartInstance = ec;
    this.ngOnChanges();
  }

  //#region Settings
  /**
   * On setting click open popup to execute a serie of possible actions on the slices defined for the day the component is associated to.
   *
   * These actions are the following:
   *
   * * COPY: Copy the configuration of the currently displayed day to one or more other days. It does so by overwriting all the "weeklyPlanningActions"
   * (IE. the minimal unit-time-sections) characterized by the target days-code with the equivalent weeklyPlanningActions associted to the displayed day.
   *
   * * ADD: Add a new time-slice to the currently displayed day, modifyng the needed "weeklyPlanningActions" (IE. the minimal unit-time-sections)
   * associated to it.
   *
   * * CHANGEDAY: (ONLY IF COMPONENT IS INSERTED INSIDE A SUPERVISION) Manually change the list of "weeklyPlanningActions" displayed to the ones associted with
   * another day WITHOUT destroy the entire component.
   */
  public async settingClick() {
    if (this.editWeeklyPlannings) {
      const popover = await this._popoverCtrl.create({
        component: PopoverObjectInfoComponent,
        componentProps: {
          objWeeklyPlanning: true,
          fromCustomInterface: this.inCustomInterface,
        },
        event: event,
        translucent: true,
        animated: true,
        showBackdrop: false,
      });

      await popover.present();

      popover.onWillDismiss().then(async (operation) => {
        switch (operation.data) {
          case "COPY":

            const modalDay = await this._modalController.create({
              component: WeekDayChooseComponent,
              componentProps: {
                disabledDays: [this.day],
              },
              cssClass: "modal-fit-height",
            });

            modalDay.onDidDismiss().then(async (daysSelected) => {
              if (
                daysSelected.data &&
                daysSelected.data != null &&
                daysSelected.data.length > 0
              ) {
                await this._loaderService.presentLoaderWaiting();
                try {
                  await this._weeklyPlanningService.CopyOnDays(
                    this.currentWeeklyPlanning.id,
                    this.day,
                    daysSelected.data
                  );
                } catch (error) {
                  await this._alertService.presentAlertSomethingWrong();
                } finally {
                  await this._loaderService.dismissLoaderWaiting();
                }
              }
            });

            return await modalDay.present();
            break;

          case "ADD":
            this.askNewSlice(this.getStateFromTName("T1"));
            break;

          case "CHANGEDAY":

            const modalDayChange = await this._modalController.create({
              component: WeekDayChooseComponent,
              componentProps: {
                disabledDays: [this.day],
                singleSelection: true,
                titleKey: "dictionary.change_day",
              },
              cssClass: "modal-fit-height",
            });

            modalDayChange.onDidDismiss().then(async (daysSelected) => {
              if (
                daysSelected.data &&
                daysSelected.data != null &&
                daysSelected.data.length > 0
              ) {
                this.day = daysSelected.data[0];
                this.loadData();
                this.loadTranslation();
              }
            });

            return await modalDayChange.present();
            break;

          default:
            break;
        }
      });
    }
  }
  //#endregion

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.monday",
        "dictionary.tuesday",
        "dictionary.wednesday",
        "dictionary.thursday",
        "dictionary.friday",
        "dictionary.saturday",
        "dictionary.sunday",
        "dictionary.from",
        "dictionary.to",
        "dictionary.chooseOperation",
        "dictionary.chooseOperationMessage",
        "dictionary.changeValue",
        "dictionary.changeColor",
        "dictionary.add_sector",
        "dictionary.operation",
        "dictionary.operationOnAddress",
        "dictionary.read",
        "dictionary.write",
        "dictionary.writeAddress",
        "dictionary.valueToWrite",
        "dictionary.removeValueAsk",
        "dictionary.valueUsed",
        "dictionary.weeklyPlanningNoChange",
      ])
      .subscribe((translation) => {
        this.from = translation["dictionary.from"];
        this.to = translation["dictionary.to"];
        this.monday = translation["dictionary.monday"];
        this.tuesday = translation["dictionary.tuesday"];
        this.wednesday = translation["dictionary.wednesday"];
        this.thursday = translation["dictionary.thursday"];
        this.friday = translation["dictionary.friday"];
        this.saturday = translation["dictionary.saturday"];
        this.sunday = translation["dictionary.sunday"];
        this._lblTitleChooseOperation =
          translation["dictionary.chooseOperation"];
        this._lblMessageChooseOperation =
          translation["dictionary.chooseOperationMessage"];
        this._lblOptionChangeValue = translation["dictionary.changeValue"];
        this._lblOptionChangeColor = translation["dictionary.changeColor"];
        this._lblOptionAddSlice = translation["dictionary.add_sector"];
        this._lblMOp = translation["dictionary.operation"];
        this._lblOp2 = translation["dictionary.operationOnAddress"];
        this._lblRead = translation["dictionary.read"];
        this._lblWrite = translation["dictionary.write"];
        this._lblWriteAddress = translation["dictionary.writeAddress"];
        this._lblValueToWrite = translation["dictionary.valueToWrite"];
        this._lblRemoveValue = translation["dictionary.removeValueAsk"];
        this._lblValueAlreadyUsed = translation["dictionary.valueUsed"];
        this._lblNoChange = translation["dictionary.weeklyPlanningNoChange"];

        this.setDayLabel();
      });
  }

  /**
   * Set the translated week-day name shown in the card header.
   */
  private setDayLabel(): void {
    switch (this.day) {
      case 1:
        this.dayLabel = this.monday;
        break;
      case 2:
        this.dayLabel = this.tuesday;
        break;
      case 3:
        this.dayLabel = this.wednesday;
        break;
      case 4:
        this.dayLabel = this.thursday;
        break;
      case 5:
        this.dayLabel = this.friday;
        break;
      case 6:
        this.dayLabel = this.saturday;
        break;
      case 7:
        this.dayLabel = this.sunday;
        break;
      default:
        break;
    }
  }

  /**
   * Format a progressive day-minute (0..1440) as "HH:mm".
   * @param totalMinutes minute of the day
   * @returns zero-padded "HH:mm" string
   */
  private formatMinute(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, "0");
    const minutes = (totalMinutes % 60).toString().padStart(2, "0");
    return hours + ":" + minutes;
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  private subscribeWeeklyPlanningService(): void {
    this._weeklyPlanningSub = this._weeklyPlanningService.weeklyPlanningList
      .pipe(filter((res) => res !== null))
      .subscribe(
        async (listWeeklyPlanning: Array<WeeklyPlanning>) => {
          if (
            !this._weeklyPlanningService
              .isErrorInGetWeeklyPlanningListAPIRequest
          ) {
            this.ngOnChanges();
          }
        },
        (error) => {
          //ERRORE
        }
      );
  }

  /**
   * Open the popup to choose the address value on the base of the address kind and the available values
   * @param t
   */
  private async openAddressValue(t: number) {
    if (this.currentConnectionType != ConnectionType.NONE) {
      let commandToWrite = await this.openPopupSelectValueAddress(false);
      if (
        commandToWrite.trim() !== "" &&
        commandToWrite === ClickActionType.WRITE
      ) {
        const resultOperation = await this._alertService.presentPrompt(
          this._lblWrite,
          this._lblWriteAddress,
          this._lblValueToWrite,
          this._addressWeeklyPlanning.hiLevelDataFormat
        );
        if (resultOperation !== false) {
          commandToWrite = resultOperation.toString();
          commandToWrite =
            this._addressWeeklyPlanning.ParseNewValue(commandToWrite);
        } else {
          commandToWrite = "";
        }
      }

      if (commandToWrite !== "") {
        let value = StaticClass.GetStateNameByValue(
          this._addressWeeklyPlanning.states,
          commandToWrite
        );

        if (!this.isValueUsed(commandToWrite, t)) {
          // patch value
          switch (t) {
            case 1:
              this.currentWeeklyPlanning.t1 = commandToWrite;
              break;
            case 2:
              this.currentWeeklyPlanning.t2 = commandToWrite;
              break;
            case 3:
              this.currentWeeklyPlanning.t3 = commandToWrite;
              break;
            case 4:
              this.currentWeeklyPlanning.t4 = commandToWrite;
              break;
            case 5:
              this.currentWeeklyPlanning.t5 = commandToWrite;
              break;
            default:
              break;
          }

          this._weeklyPlanningService.UpdateweeklyPlanning(
            this.currentWeeklyPlanning.id,
            this.currentWeeklyPlanning
          );

          this._eventService.OnReloadWeeklyPlanning.next();
        } else {
          // show popup value is already used
          await this._alertService.presentAlertOk(
            this._lblValueAlreadyUsed,
            "",
            ""
          );
        }
      }
    } else {
      await this._alertService.presentAlertCommandNotAvaiable();
      await this._alertService.clearAlertCommandNotAvaiable();
    }
  }

  /**
   * Returns true if the value has already been used
   * @param value value to check
   * @param t number of the value
   * @returns true if value has not been used, false otherwise
   */
  private isValueUsed(value: string, t: number): boolean {
    let used: boolean = false;
    switch (t) {
      case 1:
        used =
          value == this.currentWeeklyPlanning.t2 ||
          value == this.currentWeeklyPlanning.t3 ||
          value == this.currentWeeklyPlanning.t4 ||
          value == this.currentWeeklyPlanning.t5;
        break;
      case 2:
        used =
          value == this.currentWeeklyPlanning.t1 ||
          value == this.currentWeeklyPlanning.t3 ||
          value == this.currentWeeklyPlanning.t4 ||
          value == this.currentWeeklyPlanning.t5;
        break;
      case 3:
        used =
          value == this.currentWeeklyPlanning.t2 ||
          value == this.currentWeeklyPlanning.t1 ||
          value == this.currentWeeklyPlanning.t4 ||
          value == this.currentWeeklyPlanning.t5;
        break;
      case 4:
        used =
          value == this.currentWeeklyPlanning.t2 ||
          value == this.currentWeeklyPlanning.t3 ||
          value == this.currentWeeklyPlanning.t1 ||
          value == this.currentWeeklyPlanning.t5;
        break;
      case 5:
        used =
          value == this.currentWeeklyPlanning.t2 ||
          value == this.currentWeeklyPlanning.t3 ||
          value == this.currentWeeklyPlanning.t4 ||
          value == this.currentWeeklyPlanning.t1;
        break;
    }
    return used;
  }

  /**
   * Return the TX value on the base of the state of the address
   * @param State
   * @returns
   */
  private getTNameFromState(State: string) {
    switch (State.trim()) {
      case StaticClass.GetStateNameByValue(
        this._addressWeeklyPlanning.methods,
        this.currentWeeklyPlanning.t1,
        true,
        this._addressWeeklyPlanning.measureUnit
      ).trim():
        return "T1";
        break;
      case StaticClass.GetStateNameByValue(
        this._addressWeeklyPlanning.methods,
        this.currentWeeklyPlanning.t2,
        true,
        this._addressWeeklyPlanning.measureUnit
      ).trim():
        return "T2";
        break;
      case StaticClass.GetStateNameByValue(
        this._addressWeeklyPlanning.methods,
        this.currentWeeklyPlanning.t3,
        true,
        this._addressWeeklyPlanning.measureUnit
      ).trim():
        return "T3";
        break;
      case StaticClass.GetStateNameByValue(
        this._addressWeeklyPlanning.methods,
        this.currentWeeklyPlanning.t4,
        true,
        this._addressWeeklyPlanning.measureUnit
      ).trim():
        return "T4";
        break;
      case StaticClass.GetStateNameByValue(
        this._addressWeeklyPlanning.methods,
        this.currentWeeklyPlanning.t5,
        true,
        this._addressWeeklyPlanning.measureUnit
      ).trim():
        return "T5";
        break;
      default:
        "T1";
        break;
    }
  }

  /**
   * Get weekly planning value from TX
   * @param TValue
   * @returns
   */
  getTValueFromTName(TValue: string): string {
    switch (TValue) {
      case "T1":
        return this.currentWeeklyPlanning.t1;
        break;
      case "T2":
        return this.currentWeeklyPlanning.t2;
        break;
      case "T3":
        return this.currentWeeklyPlanning.t3;
        break;
      case "T4":
        return this.currentWeeklyPlanning.t4;
        break;
      case "T5":
        return this.currentWeeklyPlanning.t5;
        break;
      default:
        return this.currentWeeklyPlanning.t1;
        break;
    }
  }

  /**
   * Get state of the address on the base of the TX
   * @param TName
   * @returns
   */
  private getStateFromTName(TName: string): string {
    switch (TName) {
      case "T1":
        return StaticClass.GetStateNameByValue(
          this._addressWeeklyPlanning.methods,
          this.currentWeeklyPlanning.t1,
          true,
          this._addressWeeklyPlanning.measureUnit
        ).trim();
        break;
      case "T2":
        return StaticClass.GetStateNameByValue(
          this._addressWeeklyPlanning.methods,
          this.currentWeeklyPlanning.t2,
          true,
          this._addressWeeklyPlanning.measureUnit
        ).trim();
        break;
      case "T3":
        return StaticClass.GetStateNameByValue(
          this._addressWeeklyPlanning.methods,
          this.currentWeeklyPlanning.t3,
          true,
          this._addressWeeklyPlanning.measureUnit
        ).trim();
        break;
      case "T4":
        return StaticClass.GetStateNameByValue(
          this._addressWeeklyPlanning.methods,
          this.currentWeeklyPlanning.t4,
          true,
          this._addressWeeklyPlanning.measureUnit
        ).trim();
        break;
      case "T5":
        return StaticClass.GetStateNameByValue(
          this._addressWeeklyPlanning.methods,
          this.currentWeeklyPlanning.t5,
          true,
          this._addressWeeklyPlanning.measureUnit
        ).trim();
        break;
      default:
        return StaticClass.GetStateNameByValue(
          this._addressWeeklyPlanning.methods,
          this.currentWeeklyPlanning.t1,
          true,
          this._addressWeeklyPlanning.measureUnit
        ).trim();
        break;
    }
  }

  //NOT USED
  /**
   * Get the TX name from the value of the weeklyplanning
   */
  getTNameFromTValue(TValue: string): string {
    switch (TValue) {
      case this.currentWeeklyPlanning.t1:
        return "T1";
        break;
      case this.currentWeeklyPlanning.t2:
        return "T2";
        break;
      case this.currentWeeklyPlanning.t3:
        return "T3";
        break;
      case this.currentWeeklyPlanning.t4:
        return "T4";
        break;
      case this.currentWeeklyPlanning.t5:
        return "T5";
        break;
      default:
        return "T1";
        break;
    }
  }

  /**
   * Get color from TX name
   * @param TName
   * @returns
   */
  private getTColor(TName: string): string {
    switch (TName) {
      case this.getStateFromTName("T1"):
        return StaticClass.hexToRGB(this.currentWeeklyPlanning.t1Color);
        break;
      case this.getStateFromTName("T2"):
        return StaticClass.hexToRGB(this.currentWeeklyPlanning.t2Color);
        break;
      case this.getStateFromTName("T3"):
        return StaticClass.hexToRGB(this.currentWeeklyPlanning.t3Color);
        break;
      case this.getStateFromTName("T4"):
        return StaticClass.hexToRGB(this.currentWeeklyPlanning.t4Color);
        break;
      case this.getStateFromTName("T5"):
        return StaticClass.hexToRGB(this.currentWeeklyPlanning.t5Color);
        break;
      default:
        return StaticClass.hexToRGB(this.currentWeeklyPlanning.t1Color);
        break;
    }
  }

  /**
   * To avoid that clicking on legend the relative slice disappear
   * @param chart
   * @param params
   */
  private suppressSelection(chart, params) {
    chart.setOption({ animation: false });

    // Re-select what the user unselected
    chart.dispatchAction({
      type: "legendSelect",
      name: params.name,
    });

    chart.setOption({ animation: true });
  }

  /**
   * Required to show all the possible T in the legend even if there are no values in that T
   */
  private addDefaultSerie() {
    const requiredValues = [
      this.getStateFromTName("T1"),
      this.getStateFromTName("T2"),
      this.getStateFromTName("T3"),
      this.getStateFromTName("T4"),
      this.getStateFromTName("T5"),
    ];
    for (let index = 0; index < requiredValues.length; index++) {
      if (
        requiredValues[index] != "" &&
        this.echartData.findIndex((x) => x.name === requiredValues[index]) < 0
      ) {
        this.echartData.push({
          value: 0,
          name: requiredValues[index],
          startMinute: 0,
          endMinute: 0,
          itemStyle: { color: this.getTColor(requiredValues[index]) },
        });
      }
    }
  }

  /**
   * Load data from service into chart. First, it retrieves form server the updated full list of "WeekluyPlanningActions" object associted with the displaye week-day.
   * Then, the method checks the entire "lenght" of the echart component, matching it with the list of the current "weeklyPlanningActions" for the day.
   * In doing so:
   * * It create "slices", each of which starts and ends with a change in the command associated with the currently mathced Actions. The "lenght" of these slices is set throught the "value" property.
   * * It assign a  (user-friendly) "Name" to the Slices, based on the associated command.
   * * It assign a Color to the slice, based on the associated command and the user-defined setting for this.
   */
  private loadData() {
    if (this.currentWeeklyPlanning && this.currentWeeklyPlanning != null) {
      const step = 15;
      this._weeklyPlanningService
        .GetWeeklyPlanningActionByIDWeeklyPlanningAndOrDay(
          this.currentWeeklyPlanning.id,
          this.day,
          100,
          1,
          this.inCustomInterface
        )
        .then((actions: WeeklyPlanningAction[]) => {
          this.weeklyPlanningActions = actions;

          let lastCommand: string = "";

          this.echartData = [];
          lastCommand = "";
          let currentStep: number = 0;

          /**Flag identifyning the start of the currently considered "slice" */
          let startSection: number = 0;

          //CENTRAL SECTION.
          for (let index = 0; index < actions.length; index++) {
            if (lastCommand === actions[index].commands) {
              currentStep += step;
            } else {
              if (index > 0) {
                this.echartData.push({
                  value: actions[index].progressivetime - startSection,
                  name: this.getStateFromTName(lastCommand),
                  startMinute: startSection,
                  endMinute: actions[index].progressivetime,
                  itemStyle: {
                    color: this.getTColor(this.getStateFromTName(lastCommand)),
                  },
                });
              }

              lastCommand = actions[index].commands;
              currentStep = 0;
              startSection = actions[index].progressivetime;
            }
          }

          //LAST SECTION
          this.echartData.push({
            value: 1435 - startSection,
            name: this.getStateFromTName(lastCommand),
            startMinute: startSection,
            endMinute: 1440,
            itemStyle: {
              color: this.getTColor(this.getStateFromTName(lastCommand)),
            },
          });

          this.addDefaultSerie();

          this.echartData.sort((a, b) => {
            return a.startMinute > b.startMinute
              ? 1
              : a.startMinute === b.startMinute
                ? a.endMinute > b.endMinute
                  ? 1
                  : -1
                : -1;
          });

          this.options.series[0].data = this.echartData;
          this._chartInstance.setOption(this.options);
          this.isChartLoading = false;
        })
        .catch(() => {
          /* Release the placeholder on failure too: the chart is then left on whatever it holds,
           * which is the behaviour the card had before the placeholder existed. */
          this.isChartLoading = false;
        });
    }
  }

  /**
   * Create/update a sigle "time-slice" (and all its inner miminal "unit-time-sectors") of the currently selected weekly-planning
   * object. It defines (or update) its inferior and superior temporal limits (which correspond to the starting Minute/"progressivetime" 
   * of, respectively, the first unit-time-sector of the slice itself and the one just after its conclusion) and/or its associated action.
   * 
   * The "UPDATE existing sector" function of the method is executed if the parameters "PreviousStartMinute"/"PreviousEndMinute" are MEANINGFUL, therefore are 
   * different respectively from "StartMinute"/"EndMinute", which happens when the method is invocated in hook "onClick()" (this means that the user has 
   * selected an existing slice from the graphic). In this case, the inferior/superior limits of the slice will be adjusted to the new values, "extending" the 
   * other slices surrounding the one selected.
   * 
   * Otherwise, it means that the method must "CREATE a new slice" and it has been called inside "askNewSlice()" (this means that the user has 
   *elected to create a new slice by using the contextual menu above the graphic). In this case, the single unit-time-sectors included inside the inferior adn superior limits 
   of the new slice shall be simply overwritten with the new command.
   * @param TValue code for the new action
   * @param StartMinute current starting minute of the "time-slice"; it is defined as the "x" minute of a  day composed by 1440-minutes (IE.24-hours) 
   * @param EndMinute current end minute of the "time-slice"; it is defined as the "x" minute of a  day composed by 1440-minutes (IE.24-hours)
   * @param PreviousStartMinute previously defined starting minute of the "time-slice"; it is defined as the "x" minute of a  day composed by 1440-minutes (IE.24-hours). IF this
   * @param PreviousEndMinute previously defined end minute of the "time-slice"; it is defined as the "x" minute of a  day composed by 1440-minutes (IE.24-hours)
   */
  private async createNewSlice(
    TValue: string,
    StartMinute: number,
    EndMinute: number,
    PreviousStartMinute: number,
    PreviousEndMinute: number
  ) {
    let idx = 0;

    /** Total unit-time-sectors actually persisted across the (up to three) sub-slice updates below. */
    let updatedSectors = 0;

    // Show the app loader for the whole burst of PUTs: a slice change can persist dozens of 15-minute
    // sectors, so without visible feedback the UI looks frozen while the requests fly.
    await this._loaderService.presentLoaderWaiting();

    try {
      //#region UPDATE EXISTING SLICE: OPTIONALE EXTENSION OF THE "TIME-SLICE" THAT COME BEFORE.
      /*If the selected "TIME-SECTOR" was already existing and current starting minute is higher than the previous one ("shortening"
      the time-sector), we extends the time-sector that comes just before the one selected.
      */
      if (PreviousStartMinute < StartMinute) {
        let previousTValue = "";
        idx = this.weeklyPlanningActions.findIndex(
          (wpa) => wpa.progressivetime === PreviousStartMinute
        );

        //NOTE: If the Action starting with "PreviousStartMinute" is the first for the current day (IE. it is the first element in the list of all
        //the current day weekluPlanningActions), that means that there are no "time-sector that comes just before the one selected"; therefore, we do nothing.
        //Otherwaise, we proceed.
        if (idx > 0) {
          previousTValue = this.weeklyPlanningActions[idx - 1].commands;

          updatedSectors += await this.updateSubSlice(
            PreviousStartMinute,
            StartMinute,
            previousTValue
          );
        }
      }
      //#endregion

      //#region UPDATE EXISTING SLICE/CREATING NEW SLICE
      updatedSectors += await this.updateSubSlice(
        StartMinute,
        EndMinute,
        TValue
      );

      //#endregion

      //#region UPDATE EXISTING SLICE: OPTIONAL EXTENSION OF  "TIME-SLICE" THAT COME AFTER.
      /*If the selected "TIME-SECTOR" was already existing and current end minute is higher than the previous one ("shortening"
      the time-sector), we extends the time-sector that comes just after the one selected.
      */
      if (PreviousEndMinute > EndMinute) {
        let nextTValue = "";

        if (PreviousEndMinute >= 1440) {
          //In this case, the immediately-next Action object (which shall be used to take the command to be inserted) is one
          //of the first Actions of the following week-day.
          let nextActionIndex = 0;

          const nextDayActions =
            this._weeklyPlanningService.getNextDayFirstActions(
              this.currentWeeklyPlanning.id,
              this.day
            );

          nextTValue = nextDayActions[nextActionIndex].commands;
        } else {
          //Otherwise, the immediately-next Action object is included in the list of the Actions of the current week-day; we identifiy it
          //using the old ending of the pre-update time-slice to identify its starting minute (IE. its "progressivetime")
          idx = this.weeklyPlanningActions.findIndex(
            (wpa) => wpa.progressivetime === PreviousEndMinute
          );

          nextTValue = this.weeklyPlanningActions[idx].commands;
        }

        updatedSectors += await this.updateSubSlice(
          EndMinute,
          PreviousEndMinute,
          nextTValue
        );
      }
      //#endregion
    } catch (error) {
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
    }

    // Painting the same value over a uniform slice (e.g. only the hours were tweaked on a single-value day)
    // persists nothing: tell the user the click was received but there was nothing to save, and skip the redundant reload.
    if (updatedSectors === 0) {
      await this._toastService.showToast(
        ToastType.INFO,
        this._lblNoChange,
        2000
      );
      return;
    }

    this.loadData();
  }

  /**
   * This method allows to update a temporal sub-slice associated with the currenly selected weekly-planning object.
   * It does so by sorting through each of the minimal "unit-time-sectors" which constitue the sub-slide and overwriting
   * the associated command.
   * @param startingMinuteSubSliceToUpdate  The starting minute of the "time-slice"; it is defined as the "x" minute of a  day composed by 1440-minutes (IE.24-hours). It also correspond
   * to the "progressivetime" of its first WeeklyPlanningAction.
   * @param endMinuteSubSliceToUpdate The end minute of the "time-slice"; it is defined as the "x" minute of a  day composed by 1440-minutes (IE.24-hours)
   * @param commandToOverWrite The new command to assign to each of the WeeklyPlanningAction objects (IE: the minimal "unit-time-sectors") that are contained inside the sub-slice.
   * @returns the number of unit-time-sectors whose command actually changed (and were therefore persisted); `0` when the sub-slice already held the target command.
   */
  private async updateSubSlice(
    startingMinuteSubSliceToUpdate: number,
    endMinuteSubSliceToUpdate: number,
    commandToOverWrite: string
  ): Promise<number> {
    let singleWPActionID = 0;
    let updatedSectors = 0;

    for (
      let s = startingMinuteSubSliceToUpdate;
      s < endMinuteSubSliceToUpdate;
      s += 15
    ) {
      singleWPActionID = this.weeklyPlanningActions.findIndex(
        (wpa) => wpa.progressivetime === s
      );

      if (
        this.weeklyPlanningActions[singleWPActionID].commands !==
        commandToOverWrite
      ) {
        this.weeklyPlanningActions[singleWPActionID].commands =
          commandToOverWrite;
        const dataToUpdate: iWeeklyPlanningAction = {
          Idweeklyplanning: this.currentWeeklyPlanning.id,
          Day: this.weeklyPlanningActions[singleWPActionID].day,
          Progressivetime:
            this.weeklyPlanningActions[singleWPActionID].progressivetime,
          Commands: this.weeklyPlanningActions[singleWPActionID].commands,
        };
        await this._weeklyPlanningService.UpdateWeeklyPlanningAction(
          this.weeklyPlanningActions[singleWPActionID].id,
          dataToUpdate
        );
        updatedSectors++;
      }
    }

    return updatedSectors;
  }

  /**
   * Open modal/pop-up to allow user to specify characteristics of a new "time-slice" they want to insert inside current list of "weeklyPlanningActions".
   * Please note that:
   * * It doesn't pass to the pop-up meaningful startPeriodIn/endPeriodIn parmeters, allowing the user to select whatever time-period they want.
   * * On the consequent invocation of "createNewSlice(...)", it passes as PreviousStartMinute/PreviousEndMinute parameters the same value of the StartMinute/EndMinute parameters.
   *    This is because the method is not intended to allow the update of an existing "time-slice", but  its over-writing.
   * @param defaultValue Code for the default action (IE. command) to be assigned to the soon-to-be-created "time-slice".
   * @returns
   */
  private async askNewSlice(defaultValue) {

    const modal = await this._modalController.create({
      component: DayPeriodChangeComponent,
      componentProps: {
        startPeriodIn: 0,
        endPeriodIn: 0,
        tValue: this.getTNameFromState(defaultValue),
        currentWeeklyPlanning: this.currentWeeklyPlanning,
        stringValue: this.options.legend.data,
      },
      cssClass: "modal-period-value",
    });

    modal.onDidDismiss().then(async (newValue) => {
      if (newValue.data != undefined && newValue.data != null) {
        this.createNewSlice(
          newValue.data.tValue,
          newValue.data.startPeriod,
          newValue.data.endPeriod,
          newValue.data.startPeriod,
          newValue.data.endPeriod
        );
      }
    });

    return await modal.present();
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectServerSettingsUserSub) {
      this._selectServerSettingsUserSub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Open popup value select and return selected value
   *
   * @returns
   */
  private async openPopupSelectValueAddress(
    actionWrite: boolean
  ): Promise<string> {
    const address = new Address(this._addressWeeklyPlanning);

    let listMethods: Array<AddressMethod> = address.SplitMethods(
      address.loadMethodStringByLanguage(this._translateService.getCurrentLang())
    );

    for (let i = 0; i < listMethods.length; i++) {
      if (listMethods[i].value === undefined) {
        listMethods[i].value = ClickActionType.WRITE;
      }
    }

    if (actionWrite) {
      listMethods = [
        ...listMethods,
        {
          name: this._lblWrite,
          value: ClickActionType.WRITE,
        },
      ];
    }

    let commandToWrite: string =
      await this._popupService.showModalAddressMethods(
        this._lblMOp,
        this._lblOp2,
        listMethods
      );

    return (commandToWrite ??= "");
  }
}
